// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tab,
  Typography,
} from '@mui/material'
import { PerfTabEnum } from '@shared/Enums'
import { PoolOverviewCTOSchema } from '@shared/sharedTypes'

import { TotalDepositsAllPools } from '@react/components/charts/TotalDepositsAllPools'
import { TotalPrincipalAllPools } from '@react/components/charts/TotalPrincipalAllPools'
import { TotalPrincipalFlex } from '@react/components/charts/TotalPrincipalFlex'
import { TotalPrincipalTerm } from '@react/components/charts/TotalPrincipalTerm'
import { TotalWithdrawalsAllPools } from '@react/components/charts/TotalWithdrawalsAllPools'
import { fetchParticipantsInPools, fetchPools } from '@react/fetchers/PoolFetchers'
import { useOtChainState } from '@react/redux/store'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { EthAddr } from '@shared/SchemaTypes'
import React, { useEffect } from 'react'
import * as yup from 'yup'

export const LenderExplorerSchema = yup.object({
  poolOverviewCTO: PoolOverviewCTOSchema,
})

export default function PerformancePage(): React.ReactElement {
  const [selectedTab, setSelectedTab] = React.useState<PerfTabEnum>(PerfTabEnum.OVERVIEW)
  const [selectedLender, setSelectedLender] = React.useState<string>('')
  const [selectedCurrency, setSelectedCurrency] = React.useState<string>('')
  const [selectedPool, setSelectedPool] = React.useState<string>('')
  const [lenderAddrs, setLenderAddrs] = React.useState<EthAddr[]>([])
  const [poolAddrs, setPoolAddrs] = React.useState<EthAddr[]>([])
  const [totalOutstanding, setTotalOutstanding] = React.useState<number>(0)
  const [totalOutstandingTerm, setTotalOutstandingTerm] = React.useState<number>(0)
  const [totalOutstandingFlex, setTotalOutstandingFlex] = React.useState<number>(0)
  const [deposit30, setDeposit30] = React.useState<number>(0)
  const [deposit90, setDeposit90] = React.useState<number>(0)
  const [deposit180, setDeposit180] = React.useState<number>(0)
  const [deposit360, setDeposit360] = React.useState<number>(0)
  const [depositYTD, setDepositYTD] = React.useState<number>(0)
  const [depositAllTime, setDepositAllTime] = React.useState<number>(0)
  const [withdrawal30, setWithdrawal30] = React.useState<number>(0)
  const [withdrawal90, setWithdrawal90] = React.useState<number>(0)
  const [withdrawal180, setWithdrawal180] = React.useState<number>(0)
  const [withdrawal360, setWithdrawal360] = React.useState<number>(0)
  const [withdrawalYTD, setWithdrawalYTD] = React.useState<number>(0)
  const [withdrawalAllTime, setWithdrawalAllTime] = React.useState<number>(0)

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    chainConfiguration: { chainName, chainConfigurationName, providerReadOnly },
  } = useOtChainState()

  const handleChange = (_event: React.SyntheticEvent, newValue: PerfTabEnum) => {
    setSelectedTab(newValue)
  }

  const exportCSV = () => {
    if (selectedTab === PerfTabEnum.OVERVIEW) {
      const csvData = [
        [
          'Vault Address',
          'Current Outstanding Loan Principal (All Vaults)',
          'Current Outstanding Loan Principal (Flex Vaults)',
          'Current Outstanding Loan Principal (Term Vaults)',
        ],
        [
          selectedPool,
          erc20WeiToCommaDecString(totalOutstanding),
          erc20WeiToCommaDecString(totalOutstandingTerm),
          erc20WeiToCommaDecString(totalOutstandingFlex),
        ],
      ]

      // Convert the CSV data array to a string
      const csvContent = csvData.map((row) => row.map((value) => `"${value}"`).join(',')).join('\n')

      // Create a Blob with the CSV content and set its MIME type to text/csv
      const blob = new Blob([csvContent], { type: 'text/csv' })

      // Create a temporary anchor (<a>) element to trigger the download
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'performance_data_overview.csv'

      // Append the link to the body, trigger a click event to download, and then remove the link
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    if (selectedTab === PerfTabEnum.DEPOSITS) {
      const csvData = [
        [
          'Vault Address',
          'Lender Address',
          'Total Deposits 30 days',
          'Total Deposits 90 days',
          'Total Deposits 180 days',
          'Total Deposits 360 days',
          'Total Deposits YTD',
          'Total Deposits All Time',
        ],
        [
          selectedPool,
          selectedLender,
          erc20WeiToCommaDecString(deposit30),
          erc20WeiToCommaDecString(deposit90),
          erc20WeiToCommaDecString(deposit180),
          erc20WeiToCommaDecString(deposit360),
          erc20WeiToCommaDecString(depositYTD),
          erc20WeiToCommaDecString(depositAllTime),
        ],
      ]

      // Convert the CSV data array to a string
      const csvContent = csvData.map((row) => row.map((value) => `"${value}"`).join(',')).join('\n')

      // Create a Blob with the CSV content and set its MIME type to text/csv
      const blob = new Blob([csvContent], { type: 'text/csv' })

      // Create a temporary anchor (<a>) element to trigger the download
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'performance_data_deposits.csv'

      // Append the link to the body, trigger a click event to download, and then remove the link
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    if (selectedTab === PerfTabEnum.WITHDRAWALS) {
      const csvData = [
        [
          'Vault Address',
          'Lender Address',
          'Total Withdrawals 30 days',
          'Total Withdrawals 90 days',
          'Total Withdrawals 180 days',
          'Total Withdrawals 360 days',
          'Total Withdrawals YTD',
          'Total Withdrawals All Time',
        ],
        [
          selectedPool,
          selectedLender,
          erc20WeiToCommaDecString(withdrawal30),
          erc20WeiToCommaDecString(withdrawal90),
          erc20WeiToCommaDecString(withdrawal180),
          erc20WeiToCommaDecString(withdrawal360),
          erc20WeiToCommaDecString(withdrawalYTD),
          erc20WeiToCommaDecString(withdrawalAllTime),
        ],
      ]

      // Convert the CSV data array to a string
      const csvContent = csvData.map((row) => row.map((value) => `"${value}"`).join(',')).join('\n')

      // Create a Blob with the CSV content and set its MIME type to text/csv
      const blob = new Blob([csvContent], { type: 'text/csv' })

      // Create a temporary anchor (<a>) element to trigger the download
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'performance_data_deposits.csv'

      // Append the link to the body, trigger a click event to download, and then remove the link
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const isProduction = chainName === 'ethereum' || chainName === 'avalanche'
  const currencies = isProduction ? ['USDC', 'EURC'] : ['MUSDC', 'MEURC']
  const yearToDateDays = (() => {
    const startOfYear = new Date(new Date().getFullYear(), 0, 1)
    const today = new Date()
    const millisecondsInADay = 1000 * 60 * 60 * 24
    return Math.floor((today.getTime() - startOfYear.getTime()) / millisecondsInADay)
  })()

  useEffect(() => {
    async function initializePools() {
      const poolAddrs = (await fetchPools(serverApiBaseUrl, restAPIKey, chainConfigurationName)).map((p) => p.poolAddr)
      const addrs = await fetchParticipantsInPools(serverApiBaseUrl, restAPIKey, poolAddrs)
      setLenderAddrs(addrs)
      setPoolAddrs(poolAddrs)
      setSelectedLender('All Lenders')
      setSelectedCurrency(currencies[0])
      setSelectedPool('All Vaults')
    }
    initializePools()
  }, [providerReadOnly, serverApiBaseUrl])

  return (
    <>
      <Box sx={{ paddingLeft: 5 }}>
        <TabContext value={selectedTab}>
          <TabList sx={{ marginBottom: '10px' }} onChange={handleChange} variant="scrollable" scrollButtons="auto">
            <Tab label={'Overview'} value={PerfTabEnum.OVERVIEW} />
            <Tab label={'Deposits'} value={PerfTabEnum.DEPOSITS} />
            <Tab label={'Withdrawals'} value={PerfTabEnum.WITHDRAWALS} />
          </TabList>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl sx={{ m: 1, width: 200 }}>
                <InputLabel id="lender-label">Lender</InputLabel>
                <Select
                  style={{ width: 200 }}
                  labelId="lender-label"
                  id="lender-select"
                  input={<OutlinedInput id="lender-select" label="Lender" />}
                  value={selectedLender ?? ('' as string)}
                  renderValue={(value: string) => {
                    return (
                      <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                        {value}
                      </Typography>
                    )
                  }}
                  onChange={(event: SelectChangeEvent) => {
                    if (event.target.value === 'All Lenders') {
                      setSelectedLender('All Lenders')
                    } else {
                      setSelectedLender(event.target.value as EthAddr)
                      if (!lenderAddrs.includes('All Lenders')) {
                        lenderAddrs.unshift('All Lenders')
                      }
                    }
                  }}
                >
                  {lenderAddrs.map((value) => {
                    return (
                      <MenuItem
                        value={value}
                        key={`${value}`}
                        style={{
                          paddingLeft: 12,
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                        }}
                      >
                        <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                          {value}
                        </Typography>
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
            </Grid>
            {selectedTab && selectedTab !== PerfTabEnum.OVERVIEW && (
              <Grid item xs={12} md={2}>
                <FormControl sx={{ m: 1, width: 200 }}>
                  <InputLabel id="vault">Vault</InputLabel>
                  <Select
                    style={{ width: 200 }}
                    labelId="vault"
                    id="vault-select"
                    input={<OutlinedInput id="vault-select" label="Vault" />}
                    value={selectedPool ?? ('' as string)}
                    renderValue={(value: string) => {
                      return (
                        <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                          {value}
                        </Typography>
                      )
                    }}
                    onChange={(event: SelectChangeEvent) => {
                      if (event.target.value === 'All Vaults') {
                        setSelectedPool('All Vaults')
                      } else {
                        setSelectedPool(event.target.value as EthAddr)
                        if (!poolAddrs.includes('All Vaults')) {
                          poolAddrs.unshift('All Vaults')
                        }
                      }
                    }}
                  >
                    {poolAddrs.map((value) => {
                      return (
                        <MenuItem
                          value={value}
                          key={`${value}`}
                          style={{
                            paddingLeft: 12,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                          }}
                        >
                          <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                            {value}
                          </Typography>
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={2}>
              <FormControl sx={{ m: 1, width: 200 }}>
                <InputLabel id="currency">Currency</InputLabel>
                <Select
                  style={{ width: 200 }}
                  labelId="currency"
                  id="currency-select"
                  input={<OutlinedInput id="currency-select" label="Currency" />}
                  value={selectedCurrency ?? ('' as string)}
                  renderValue={(value: string) => {
                    return (
                      <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                        {value}
                      </Typography>
                    )
                  }}
                  onChange={(event: SelectChangeEvent) => {
                    setSelectedCurrency(event.target.value as string)
                  }}
                >
                  {currencies.map((value) => {
                    return (
                      <MenuItem
                        value={value}
                        key={`${value}`}
                        style={{
                          paddingLeft: 12,
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                        }}
                      >
                        <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                          {value}
                        </Typography>
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl sx={{ m: 1, width: 200 }}>
                <Button
                  sx={{
                    marginLeft: '4px',
                    height: '50px',
                    textTransform: 'none',
                    marginRight: '15px',
                    color: '#171717',
                    backgroundColor: '#EEEEEE',
                  }}
                  variant="contained"
                  onClick={exportCSV}
                >
                  Export CSV
                </Button>
              </FormControl>
            </Grid>
            {selectedTab && selectedTab === PerfTabEnum.OVERVIEW && <Grid item xs={12} md={2}></Grid>}
            <Grid item xs={12} md={2}></Grid>
            <Grid item xs={12} md={4}>
              {selectedTab === PerfTabEnum.OVERVIEW && (
                <TotalPrincipalAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  callback={setTotalOutstanding}
                />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {selectedTab === PerfTabEnum.OVERVIEW && (
                <TotalPrincipalFlex
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  callback={setTotalOutstandingFlex}
                />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {selectedTab === PerfTabEnum.OVERVIEW && (
                <TotalPrincipalTerm
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  callback={setTotalOutstandingTerm}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.DEPOSITS && (
                <TotalDepositsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={30}
                  poolAddr={selectedPool}
                  callback={setDeposit30}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.DEPOSITS && (
                <TotalDepositsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={90}
                  poolAddr={selectedPool}
                  callback={setDeposit90}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.DEPOSITS && (
                <TotalDepositsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={180}
                  poolAddr={selectedPool}
                  callback={setDeposit180}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.DEPOSITS && (
                <TotalDepositsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={360}
                  poolAddr={selectedPool}
                  callback={setDeposit360}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.DEPOSITS && (
                <TotalDepositsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={yearToDateDays}
                  yearToDate={true}
                  poolAddr={selectedPool}
                  callback={setDepositYTD}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.DEPOSITS && (
                <TotalDepositsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={2000}
                  allTime={true}
                  poolAddr={selectedPool}
                  callback={setDepositAllTime}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}></Grid>
            <Grid item xs={12} md={3}></Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.WITHDRAWALS && (
                <TotalWithdrawalsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={30}
                  poolAddr={selectedPool}
                  callback={setWithdrawal30}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.WITHDRAWALS && (
                <TotalWithdrawalsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={90}
                  poolAddr={selectedPool}
                  callback={setWithdrawal90}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.WITHDRAWALS && (
                <TotalWithdrawalsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={180}
                  poolAddr={selectedPool}
                  callback={setWithdrawal180}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.WITHDRAWALS && (
                <TotalWithdrawalsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={360}
                  poolAddr={selectedPool}
                  callback={setWithdrawal360}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.WITHDRAWALS && (
                <TotalWithdrawalsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={yearToDateDays}
                  yearToDate={true}
                  poolAddr={selectedPool}
                  callback={setWithdrawalYTD}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedTab === PerfTabEnum.WITHDRAWALS && (
                <TotalWithdrawalsAllPools
                  lenderAddr={selectedLender}
                  currency={selectedCurrency}
                  lengthDays={2000}
                  allTime={true}
                  poolAddr={selectedPool}
                  callback={setWithdrawalAllTime}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}></Grid>
            <Grid item xs={12} md={3}></Grid>
          </Grid>
        </TabContext>
      </Box>
    </>
  )
}
